<script setup lang="ts">
import type { Dtwin } from "~/modules/dtwin/models/Dtwin.interface";
import { DtwinConfigurationType } from "~/modules/dtwin/models/DtwinConfigurationType.enum";
import type { DynamicUIDetail } from "~/modules/dtwin/models/DynamicUIDetail.interface";
import ILxcEye from "~icons/lxc/eye";
import ILxcShare from "~icons/lxc/share";

const props = defineProps<{
  dtwin?: Dtwin | null;
  configurationUiConfig?: Record<string, Record<string, DynamicUIDetail[]>>;
  organizationCode?: number;
}>();

const emit = defineEmits(["configurationUploaded"]);

const { t } = useI18n();

const showImportPanel = ref(false);
const showConfigurationListPanel = ref(false);
const reloadConfigurationList = ref(false);

const isSelfSupporting: ComputedRef<boolean> = computed(
  () =>
    props.dtwin?.configurationType === DtwinConfigurationType.SELF_SUPPORTING,
);

const browseButtonTranslation = computed(() => {
  if (isSelfSupporting.value) {
    return t("dtwins.form.configuration.browse.selfSupporting");
  } else {
    return t("dtwins.form.configuration.browse.specific");
  }
});

// IDs of the version fields that need conditional display based on configurationType
const VERSION_FIELD_IDS = {
  SELF_SUPPORTING: "features.configuration.reported.selfSupportingVersion",
  GLOBAL: "features.configuration.reported.globalVersion",
  SPECIFIC: "features.configuration.reported.specificVersion",
} as const;

/**
 * Filters fields based on the configuration version type of the dtwin.
 * @param fields - Array of fields to filter
 * @returns Array of filtered fields
 */
function filterFieldsByConfigurationVersionType(
  fields: DynamicUIDetail[] | undefined,
): DynamicUIDetail[] {
  if (!fields || !props.dtwin) {
    return [];
  }

  return fields.filter((field) => {
    // Check if the field is one of the version fields that need conditional display
    const isVersionField = (
      Object.values(VERSION_FIELD_IDS) as string[]
    ).includes(field.id as string);

    // If it's not a version field, always display it
    if (!isVersionField) {
      return true;
    }

    // For version fields, apply conditional logic based on configurationType
    if (isSelfSupporting.value) {
      // Show only selfSupportingVersion when configurationType is SELF_SUPPORTING
      return field.id === VERSION_FIELD_IDS.SELF_SUPPORTING;
    } else {
      // Show globalVersion and specificVersion, but not selfSupportingVersion
      return field.id !== VERSION_FIELD_IDS.SELF_SUPPORTING;
    }
  });
}

// Filter for left column fields in the information block
const filteredLeftFields = computed(() =>
  filterFieldsByConfigurationVersionType(
    props.configurationUiConfig?.information?.left,
  ),
);

// Filter for center column fields in the information block
const filteredCenterFields = computed(() =>
  filterFieldsByConfigurationVersionType(
    props.configurationUiConfig?.information?.center,
  ),
);

const onShowImportPanel = () => {
  showImportPanel.value = true;
};

const onShowConfigurationListPanel = () => {
  showConfigurationListPanel.value = true;
};

const onReloadConfigurationList = () => {
  reloadConfigurationList.value = true;
  emit("configurationUploaded");
};
</script>

<template>
  <block-container
    v-if="configurationUiConfig?.information && dtwin"
    :title="t('dtwins.form.configuration.title')"
    title-top
  >
    <template #left>
      <dtwin-form-field
        v-for="item in filteredLeftFields"
        :key="item.id"
        :dtwin="dtwin"
        :item="item"
      />
    </template>

    <template #center>
      <dtwin-form-field
        v-for="item in filteredCenterFields"
        :key="item.id"
        :dtwin="dtwin"
        :item="item"
      />
    </template>

    <template #right>
      <div class="relative">
        <div class="absolute flex gap-4 right-0">
          <lxc-button
            type="tertiary"
            html-type="button"
            :title="browseButtonTranslation"
            class="!px-3 !py-3"
            @click="onShowConfigurationListPanel"
          >
            <i-lxc-eye class="text-gray-700 w-5 h-5" />
          </lxc-button>
          <lxc-button
            type="tertiary"
            html-type="button"
            :title="t('dtwins.form.configuration.upload.buttonTooltip')"
            class="!px-3 !py-3"
            @click="onShowImportPanel"
          >
            <i-lxc-share class="text-gray-700 w-5 h-5" />
          </lxc-button>
        </div>
      </div>
    </template>
  </block-container>

  <dtwin-form-configuration-core-upload
    v-if="dtwin && dtwin.configurationType"
    v-model:show-import-panel="showImportPanel"
    :dtwin-uid="dtwin.uid"
    :configuration-type="dtwin?.configurationType"
    @uploaded="onReloadConfigurationList"
  />

  <dtwin-form-configuration-core-list
    v-if="dtwin && dtwin.configurationType"
    v-model:show-list-panel="showConfigurationListPanel"
    v-model:reload="reloadConfigurationList"
    :dtwin-uid="dtwin.uid"
    :configuration-type="dtwin.configurationType"
    :organization-code="organizationCode"
  />
</template>
